import { useEffect, useMemo, useState } from 'react';
import type {
  AdapterConfig,
  NavigateResultPayload,
  NavigateToNodePayload,
} from '@claudafinil/shared/contract';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { useTimelineStore } from '../../timeline/timeline-store';
import {
  useAnnotationSource,
  type UnifiedAnnotation,
} from '../hooks/use-annotation-source';
import AnnotationCard from './AnnotationCard';
import ConfirmDialog from '../../../components/shared/ConfirmDialog';

const EMPTY_STATE_MESSAGE =
  'No annotations yet. Bookmark an assistant message from the Timeline tab.';

interface AnnotationsViewProps {
  onNavigateToNode: (targetMessageText: string) => Promise<NavigateResultPayload>;
  onNavigateToAnnotation: (target: NavigateToNodePayload) => Promise<NavigateResultPayload>;
}

export default function AnnotationsView({
  onNavigateToNode,
  onNavigateToAnnotation,
}: Readonly<AnnotationsViewProps>): JSX.Element {
  const {
    annotations,
    isLoading,
    error,
    loadAnnotations,
    removeAnnotation,
    updateNote,
    clearError,
  } = useAnnotationSource();
  const currentConversationId = useTimelineStore((state) => state.conversationId);
  const activePlatform = useTimelineStore((state) => state.activePlatform);

  const [deletingAnnotation, setDeletingAnnotation] = useState<UnifiedAnnotation | null>(null);
  const [navigationError, setNavigationError] = useState<string | null>(null);

  useEffect(() => {
    if (!navigationError) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setNavigationError(null);
    }, 3000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [navigationError]);

  const sortedAnnotations = useMemo(
    () => [...annotations].sort((a, b) => b.createdAt - a.createdAt),
    [annotations],
  );

  const handleDeleteConfirm = async () => {
    if (!deletingAnnotation) {
      return;
    }

    await removeAnnotation(deletingAnnotation.id);
    setDeletingAnnotation(null);
  };

  const handleNavigate = async (annotation: UnifiedAnnotation) => {
    const platform = annotation.platform ?? activePlatform ?? 'claude';

    const result = annotation.conversationId === currentConversationId
      ? await onNavigateToNode(annotation.messageText)
      : await onNavigateToAnnotation({
        nodeId: annotation.messageId,
        conversationId: annotation.conversationId,
        messageText: annotation.messageText,
        platform: platform as AdapterConfig['platform'],
      });
    if (result.success) {
      return;
    }

    setNavigationError(
      result.error
        ?? 'Could not open this annotation. Update the extension or open the conversation manually.',
    );
  };

  const handleRetry = () => {
    clearError();
    void loadAnnotations();
  };

  if (isLoading && sortedAnnotations.length === 0) {
    return (
      <div className="flex min-h-32 items-center justify-center text-center text-[0.8125rem] text-muted-foreground">
        <p>Loading annotations...</p>
      </div>
    );
  }

  return (
    <div className="relative flex h-full flex-col gap-3 overflow-y-auto p-3">
      {navigationError ? (
        <div
          role="status"
          className="sticky top-0 z-20 rounded-md border border-destructive/30 bg-destructive/10 px-2.5 py-2 text-xs font-semibold text-destructive"
        >
          {navigationError}
        </div>
      ) : null}

      {error ? (
        <Card className="border-destructive/30">
          <CardContent>
            <p className="m-0 text-[0.8125rem] text-destructive">{error}</p>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                className="w-full sm:w-auto"
                onClick={handleRetry}
              >
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {sortedAnnotations.length === 0 ? (
        <div className="flex min-h-32 items-center justify-center text-center text-[0.8125rem] text-muted-foreground">
          <p className="m-0">{EMPTY_STATE_MESSAGE}</p>
        </div>
      ) : (
        <div className="flex flex-col gap-2.5">
          {sortedAnnotations.map((annotation) => (
            <AnnotationCard
              key={annotation.id}
              annotation={annotation}
              onNavigate={(candidate) => {
                void handleNavigate(candidate);
              }}
              onUpdateNote={updateNote}
              onDelete={setDeletingAnnotation}
            />
          ))}
        </div>
      )}

      <ConfirmDialog
        open={Boolean(deletingAnnotation)}
        title="Delete annotation"
        description={
          deletingAnnotation
            ? `Delete this bookmark from "${deletingAnnotation.conversationName}"?`
            : ''
        }
        confirmLabel="Delete"
        destructive
        onCancel={() => {
          setDeletingAnnotation(null);
        }}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
}

export { EMPTY_STATE_MESSAGE };
